export interface DeckPage {
  id: string
  label: string
}

export const deckPages: DeckPage[] = [
  { id: 'opening', label: 'Meet the Filteks' },
  { id: 'feedback', label: 'The feedback' },
  { id: 'journey', label: 'Attract. Convince. Convert.' },
  { id: 'snakkerfy', label: 'Snakkerfy the website' },
  { id: 'channels', label: 'One experience. More channels to share.' },
  { id: 'useCases', label: 'One format. Many use cases.' },
  { id: 'signals', label: 'Every interaction creates a signal' },
  { id: 'compound', label: 'Make that activity compound' },
  { id: 'station', label: 'Snakker Station' },
  { id: 'campaign', label: 'The brand-love campaign' },
]

export const screenshots = {
  websiteInvite:
    'https://cdn.magicpatterns.com/uploads/783mDKS6hytL7HBzyuoMhN/Screenshot_2026-10-22_at_09.01.23.png',
  websiteSnak:
    'https://cdn.magicpatterns.com/uploads/8CnE28cYifYcYK3933C3Bc/Screenshot_2026-10-22_at_09.02.10.png',
  snakUseCases:
    'https://cdn.magicpatterns.com/uploads/vDdm9v3Tw671K4FVieTLLT/Screenshot_2026-10-22_at_10.16.32.png',
  supremeSnak:
    'https://cdn.magicpatterns.com/uploads/7ULyWRVLjTo1J3MjTaAKWf/Screenshot_2026-10-22_at_08.39.36.png',
  campaignLoop:
    'https://cdn.magicpatterns.com/uploads/x8tL53r1QkCVLkeX7Dgbti/Screenshot_2026-10-22_at_10.17.05.png',
  controlTower:
    'https://cdn.magicpatterns.com/uploads/jYPAzbYJKofKppr5diGJM3/Screenshot_2026-10-22_at_11.02.09.png',
  easyMatchCtas:
    'https://cdn.magicpatterns.com/uploads/p8QLZfChhyE8n6HLrgKbUD/Screenshot_2026-10-22_at_09.07.20.png',
}

/** The Snak that opens from "See clinical cases". */
export const clinicalCasesSnakUrl =
  'https://content.snakker.io/snak/78Gic6GKtzqS6f3nWeH0JwurO5MiTUL8/index.html'

/** The attention-grabber example on the use-cases page. */
export const bondingSnakUrl = 'https://bonding.snakker.io'
export const attentionGrabberImage =
  'https://cdn.magicpatterns.com/uploads/sQoigfABWdWAbgUDUSwh9g/Screenshot_2026-10-22_at_11.38.02.png'

/** Example booth shown from the Congress tile on the channels page. */
export const congressBoothImage =
  'https://cdn.magicpatterns.com/uploads/riPQW3xbXUrvcwruGrT15T/Screenshot_2026-10-22_at_11.49.41.png'

/** The page that opens from "Order / Find supplier". */
export const orderPageUrl =
  'https://www.solventum.com/en-us/home/oral-care/dental-solutions/direct-procedure/composites/'

/** The procedure guide that opens from "Explore procedures". */
export const proceduresPdfUrl =
  'https://cdn.magicpatterns.com/uploads/iT8VMV8Fhkke6eUAxMDjHE/Filtek-Matrix_Step-by-Step_Composite-Technique.pdf'

/** Screenshot of the composites listing, shown in the "Order / Find supplier" window. */
export const orderPageScreenshot =
  'https://cdn.magicpatterns.com/uploads/3qKkVzX2XRGGeMK7v9Pt9r/Screenshot_2026-10-22_at_10.05.33.png'

/** Live campaign report, opened from "Campaign Data" on the signals page. */
export const campaignReportPdfUrl =
  'https://cdn.magicpatterns.com/uploads/sjG2oym4ptmVjmKz9yhB23/Filtek_case_series_-_campaign_report_20_Oct_2026.pdf'

export type CtaIcon = 'cases' | 'procedures' | 'trial' | 'order'

export interface ActionCta {
  icon: CtaIcon
  label: string
  short: string
}

export const actionCtas: ActionCta[] = [
  { icon: 'cases', label: 'Relevant clinical cases', short: 'Clinical cases' },
  {
    icon: 'procedures',
    label: 'Complete procedures and complementary products',
    short: 'Procedures',
  },
  { icon: 'trial', label: 'Product demonstrations and trial', short: 'Demo & sample' },
  { icon: 'order', label: 'Ordering and purchasing information', short: 'Order' },
]

/** Where each call to action inside the live Snak leads. */
export const snakCtaLinks: Record<CtaIcon, string> = {
  cases: clinicalCasesSnakUrl,
  procedures: proceduresPdfUrl,
  trial: orderPageUrl,
  order: orderPageUrl,
}

export type ChannelId =
  | 'embed'
  | 'messaging'
  | 'detailer'
  | 'qr'
  | 'post'
  | 'email'
  | 'advertise'
  | 'congress'

export interface ShareChannel {
  id: ChannelId
  name: string
  detail: string
}

export const shareChannels: ShareChannel[] = [
  { id: 'embed', name: 'Embed', detail: 'Websites and portals' },
  { id: 'messaging', name: 'SMS & WhatsApp', detail: 'Direct links, conversation-ready' },
  { id: 'detailer', name: 'Sales rep detailer', detail: 'Online and face-to-face presentations' },
  { id: 'qr', name: 'QR code', detail: 'Print and events' },
  { id: 'post', name: 'Post', detail: 'Organic and paid social' },
  { id: 'email', name: 'Email', detail: 'CRM and newsletters' },
  { id: 'advertise', name: 'Advertise', detail: 'Banners and paid media' },
  { id: 'congress', name: 'Congress', detail: 'Booths and sessions' },
]

export type StageId = 'attract' | 'convince' | 'convert'

export interface JourneyStage {
  id: StageId
  name: string
  channels: string
  goal: string
}

export const journey: JourneyStage[] = [
  {
    id: 'attract',
    name: 'Attract',
    channels: 'Hooks, teasers, email, banners, social and congress.',
    goal: 'Bring more relevant visitors into the experience.',
  },
  {
    id: 'convince',
    name: 'Convince',
    channels: 'Meet the Filteks — the short, entertaining, expandable lean-back Snak.',
    goal: 'Make the product family understandable, memorable and clinically relevant.',
  },
  {
    id: 'convert',
    name: 'Convert',
    channels: 'Clinical cases, procedure packages, demos, sampling and ordering.',
    goal: "Contextual CTAs connect visitors directly to Solventum's product and clinical content.",
  },
]

export type MeasureId = 'leanBack' | 'interest' | 'progress' | 'ctas' | 'sources'

export interface Measure {
  id: MeasureId
  question: string
}

export const measures: Measure[] = [
  { id: 'leanBack', question: 'How many website visitors choose the lean-back experience?' },
  { id: 'interest', question: 'Which Filtek family members generate the most interest?' },
  { id: 'progress', question: 'How far do visitors progress through the experience?' },
  { id: 'ctas', question: 'Which clinical or commercial CTAs do they select?' },
  { id: 'sources', question: 'Which external hooks and teasers bring engaged visitors back?' },
]

export const campaignStages = [
  { name: 'Hook', detail: 'A short message invites dentists in.' },
  { name: 'Snak', detail: 'A case, a choice and instant feedback.' },
  { name: 'Insight', detail: 'Every answer feeds the campaign report.' },
]

export const reuseBlocks = ['Thinking', 'Arguments', 'Evidence', 'Formats']

export const signalLoop = ['Signals', 'Next campaign', 'Better activation']

export const snakkerSupport = [
  'Adaptation',
  'Production',
  'Campaign deployment',
  'Measurement',
  'Optimization',
]

export const nextSteps = [
  { verb: 'Start', text: 'with the existing Filtek website.' },
  { verb: 'Add', text: 'the actionable Meet the Filteks experience.' },
  { verb: 'Measure', text: 'the response.' },
  { verb: 'Expand', text: 'what works.' },
]
